import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { IChatForm } from '@nimbus-global/features/chat-form/models/chat-form.model';
import { ChatFormTenantMode, ChatFormTenantResolver } from '@nimbus-global/features/chat-form/models/chat-form-tenant-resolver';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { PermissionsModel } from '@nimbus-global/features/permissions/models/permissions.model';
import { CompanionReferenceType } from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';
import { ITeamContactInformation } from '@nimbus-global/features/shared/models/api/i-team-contact-information';
import { TeamInfoService } from '@nimbus-library/features/shared/services/team/team-info.service';
import { sortByLocale } from '@nimbus-library/features/localization/utils/active-locale';
import { CompanionChatResolver as BaseCompanionChatResolver } from '@nimbus-library/features/companion-chat/services/companion-chat-resolver';
import { IChatFormEntityHandler } from '@nimbus-library/features/companion-chat/services/form-entity-handlers/i-chat-form-entity-handler';
import { TeamChatFormEntityHandler } from '@nimbus-library/features/companion-chat/services/form-entity-handlers/team-chat-form-entity-handler';

@Injectable()
export class CompanionChatResolver extends BaseCompanionChatResolver {
  public readonly formResolvers: IChatFormEntityHandler[];

  public constructor(
    private readonly authenticationService: IAuthenticationService,
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly teamInfoService: TeamInfoService,
  ) {
    super();
    this.formResolvers = [
      new TeamChatFormEntityHandler((referenceType, form, currentValues) =>
        this.resolveTeamChatReferenceOptions(referenceType, form, currentValues),
      ),
    ];
  }

  public readonly resolveTenant: ChatFormTenantResolver = () => ({
    mode: ChatFormTenantMode.Fixed,
    tenantId: this.authenticationService.getUserInfo().tenantId,
    component: null,
  });

  private resolveTeamChatReferenceOptions(
    referenceType: string,
    form: IChatForm,
    _currentValues?: Record<string, unknown>,
  ): Observable<IOptionItem[]> {
    if (referenceType !== CompanionReferenceType.Team) {
      return of([]);
    }

    const permissions = form.permissions ?? [];
    if (!permissions.length) {
      return of([]);
    }

    const { userId } = this.authenticationService.getUserInfo();
    return combineLatest([this.permissionManagerService.getBelongedTeamIds$(), this.permissionManagerService.getPermissionsModel$()]).pipe(
      first(([, permissionsModel]) => permissionsModel !== PermissionsModel.Empty),
      map(([teamIds, permissionsModel]) =>
        teamIds.filter((teamId) => permissions.every((permission) => permissionsModel.isGrantedForTeam(teamId, permission))),
      ),
      switchMap((teamIds) => (teamIds.length ? this.teamInfoService.getTeamInformation(teamIds) : of<IMap<ITeamContactInformation>>({}))),
      map((teamsById) =>
        sortByLocale(
          Object.values(teamsById)
            .filter((team) => this.teamInfoService.isTeamPickableByUser(team, userId))
            .map((team) => ({ id: team.teamId, name: team.name })),
          (option) => option.name,
        ),
      ),
    );
  }
}
